/**
 * StorybookWorkspace — guided life-story writing journey.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, ChevronLeft, ChevronRight, Check, SkipForward,
  Loader2, Users, ExternalLink, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { storybookApi } from '../../utils/storybook';
import { personHref } from '../../utils/prettyArchiveLinks';
import { getDisplayName } from '../../utils/companionProfiles';
import { StorybookReadinessPanel } from './StorybookReadinessPanel';
import { StorybookPremiumSidebar } from './StorybookPremiumSidebar';
import { StorybookPromptVoicePanel } from './StorybookPromptVoicePanel';
import { StorybookAnswerGuidance } from './StorybookAnswerGuidance';
import { StorybookAnswerMetrics } from './StorybookAnswerMetrics';
import { StorybookInterviewMode } from './StorybookInterviewMode';
import { StorybookPossessionLink } from './StorybookPossessionLink';
import { useStorybookVoice } from './useStorybookVoice';
import { ArchiveContextNav } from '../archive/ArchiveContextNav';
import { treeHref } from '../../utils/prettyArchiveLinks';
import { STORY_STUDIO_PROCESS_CHIPS } from '../../content/storyStudioContent';
import StartForMePromptBuilder from '../storyStudio/StartForMePromptBuilder';
import { Dialog, DialogContent } from '../ui/dialog';

function saveStatusLabel(status) {
  switch (status) {
    case 'saving': return 'Saving…';
    case 'saved': return 'Saved';
    case 'failed': return 'Save failed — try again';
    case 'unsaved': return 'Unsaved changes';
    default: return '';
  }
}

export function StorybookWorkspace({ storybookId, onWorkspaceLoaded }) {
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [activePromptIndex, setActivePromptIndex] = useState(0);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [linkedPeople, setLinkedPeople] = useState([]);
  const [linkedPossessionId, setLinkedPossessionId] = useState(null);
  const [treePeople, setTreePeople] = useState([]);
  const [mentionPick, setMentionPick] = useState('');
  const [mentionsOpen, setMentionsOpen] = useState(false);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [startForMeOpen, setStartForMeOpen] = useState(false);
  const autoSaveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const ws = await storybookApi.get(storybookId);
      setWorkspace(ws);
      onWorkspaceLoaded?.(ws);
      const chapters = ws.chapters || [];
      if (!activeChapterId && chapters.length) {
        setActiveChapterId(chapters[0].id);
      }
      const people = await storybookApi.people(storybookId);
      setTreePeople(people);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load storybook.');
    } finally {
      setLoading(false);
    }
  }, [storybookId, activeChapterId, onWorkspaceLoaded]);

  useEffect(() => { load(); }, [storybookId]); // eslint-disable-line react-hooks/exhaustive-deps

  const chapters = workspace?.chapters || [];
  const prompts = workspace?.prompts || [];
  const answersByPrompt = workspace?.answers_by_prompt || {};
  const progress = workspace?.progress || {};
  const readiness = workspace?.readiness || {};
  const journeySettings = workspace?.journey_settings || {};
  const nextSuggestedPrompt = workspace?.next_suggested_prompt;
  const templateMeta = workspace?.template;
  const storybook = workspace?.storybook;

  const chapterPrompts = useMemo(
    () => prompts.filter((p) => p.chapter_id === activeChapterId),
    [prompts, activeChapterId],
  );

  const activePrompt = chapterPrompts[activePromptIndex] || null;
  const activeAnswer = activePrompt ? answersByPrompt[activePrompt.id] : null;
  const activeChapter = chapters.find((c) => c.id === activeChapterId);
  const targetPerson = workspace?.target_person;
  const targetName = targetPerson ? getDisplayName(targetPerson) : 'Your story';

  useEffect(() => {
    if (!activePrompt) return;
    const ans = answersByPrompt[activePrompt.id];
    setDraftTitle(ans?.title || '');
    setDraftBody(ans?.body || ans?.transcript || '');
    setLinkedPeople(ans?.linked_people_ids || []);
    setLinkedPossessionId(ans?.linked_possession_id || null);
    setMentionPick('');
    setSaveStatus('saved');
  }, [activePrompt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const markDirty = useCallback(() => {
    setSaveStatus('unsaved');
  }, []);

  const handleStoryLinked = useCallback((storyId, answer) => {
    if (!activePrompt) return;
    setWorkspace((prev) => ({
      ...prev,
      answers_by_prompt: {
        ...prev.answers_by_prompt,
        [activePrompt.id]: { ...(prev.answers_by_prompt[activePrompt.id] || {}), ...answer, story_id: storyId },
      },
    }));
  }, [activePrompt]);

  const handleTranscript = useCallback((text) => {
    setDraftBody((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text));
    markDirty();
  }, [markDirty]);

  const voice = useStorybookVoice({
    storybookId,
    promptId: activePrompt?.id,
    draftTitle,
    storyId: activeAnswer?.story_id,
    onStoryId: handleStoryLinked,
    onTranscript: handleTranscript,
  });

  const mentionablePeople = treePeople.filter(
    (p) => p.id !== storybook?.target_person_id && !linkedPeople.includes(p.id),
  );

  const chapterCompletedCount = useMemo(() => {
    if (!activeChapterId) return 0;
    return chapterPrompts.filter((p) => answersByPrompt[p.id]?.completed).length;
  }, [chapterPrompts, answersByPrompt, activeChapterId]);

  const saveAnswer = async ({ completed = false, skipped = false } = {}) => {
    if (!activePrompt) return false;
    setSaveStatus('saving');
    try {
      const result = await storybookApi.saveAnswer(storybookId, {
        prompt_id: activePrompt.id,
        title: draftTitle.trim() || null,
        body: draftBody.trim() || null,
        linked_people_ids: linkedPeople,
        linked_possession_id: linkedPossessionId || null,
        completed,
        skipped,
      });
      setSaveStatus('saved');
      if (!completed && !skipped) {
        toast.success('Draft saved.');
      } else if (completed) {
        toast.success('Answer saved to your archive.');
      }
      setWorkspace((prev) => ({
        ...prev,
        answers_by_prompt: {
          ...prev.answers_by_prompt,
          [activePrompt.id]: result.answer,
        },
        progress: result.progress,
        readiness: result.readiness || prev.readiness,
      }));
      return true;
    } catch (err) {
      setSaveStatus('failed');
      toast.error(err?.response?.data?.detail || 'Could not save answer.');
      return false;
    }
  };

  const goNextPrompt = () => {
    if (activePromptIndex < chapterPrompts.length - 1) {
      setActivePromptIndex((i) => i + 1);
    } else {
      const chIdx = chapters.findIndex((c) => c.id === activeChapterId);
      if (chIdx < chapters.length - 1) {
        setActiveChapterId(chapters[chIdx + 1].id);
        setActivePromptIndex(0);
      }
    }
  };

  const goPrevPrompt = () => {
    if (activePromptIndex > 0) {
      setActivePromptIndex((i) => i - 1);
    } else {
      const chIdx = chapters.findIndex((c) => c.id === activeChapterId);
      if (chIdx > 0) {
        const prevChapter = chapters[chIdx - 1];
        setActiveChapterId(prevChapter.id);
        const prevPrompts = prompts.filter((p) => p.chapter_id === prevChapter.id);
        setActivePromptIndex(Math.max(0, prevPrompts.length - 1));
      }
    }
  };

  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      if (draftBody.trim() || draftTitle.trim()) {
        await saveAnswer({ completed: false });
      }
    }, 2500);
  }, [draftBody, draftTitle, linkedPeople, linkedPossessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (saveStatus !== 'unsaved') return undefined;
    scheduleAutoSave();
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [saveStatus, scheduleAutoSave]);

  const addMention = () => {
    if (!mentionPick || linkedPeople.includes(mentionPick)) return;
    setLinkedPeople((ids) => [...ids, mentionPick]);
    setMentionPick('');
    markDirty();
  };

  const removeMention = (id) => {
    setLinkedPeople((ids) => ids.filter((x) => x !== id));
    markDirty();
  };

  const goToPrompt = (suggested) => {
    if (!suggested?.prompt_id) return;
    const prompt = prompts.find((p) => p.id === suggested.prompt_id);
    if (!prompt) return;
    setActiveChapterId(prompt.chapter_id);
    const chPrompts = prompts.filter((p) => p.chapter_id === prompt.chapter_id);
    const idx = chPrompts.findIndex((p) => p.id === prompt.id);
    setActivePromptIndex(Math.max(0, idx));
  };

  const handleJourneyUpdated = (settings) => {
    setWorkspace((prev) => ({ ...prev, journey_settings: settings }));
  };

  const startInterviewAtCurrent = () => {
    setInterviewOpen(true);
  };

  const handleInterviewSave = async (andNext = false) => {
    const ok = await saveAnswer({ completed: andNext });
    if (ok && andNext) goNextPrompt();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Opening your storybook…
      </div>
    );
  }

  if (!workspace || !storybook) {
    return <div className="py-16 text-center text-muted-foreground">Storybook not found.</div>;
  }

  return (
    <div className="flex min-h-screen bg-[hsl(42_28%_97%)]" data-testid="storybook-workspace">
      {storybook?.tree_id ? (
        <ArchiveContextNav
          treeId={storybook.tree_id}
          treeLinkBase={treeHref({ id: storybook.tree_id })}
          treeName={storybook.title ? `${storybook.title}` : 'Family archive'}
          variant="tree"
          canExport={false}
        />
      ) : null}
      <div className="min-h-screen min-w-0 flex-1 bg-[hsl(42_28%_97%)]">
      <StorybookInterviewMode
        open={interviewOpen}
        onClose={() => setInterviewOpen(false)}
        prompt={activePrompt}
        promptIndex={activePromptIndex}
        totalPrompts={chapterPrompts.length}
        draftBody={draftBody}
        onDraftChange={(v) => { setDraftBody(v); markDirty(); }}
        voice={voice}
        saving={saveStatus === 'saving'}
        saveStatus={saveStatusLabel(saveStatus)}
        onSave={() => handleInterviewSave(false)}
        onSaveAndNext={() => handleInterviewSave(true)}
        onSkip={async () => {
          const ok = await saveAnswer({ skipped: true });
          if (ok) goNextPrompt();
        }}
      />

      <div className="border-b border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)]">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
                AI Story Studio
              </p>
              <p className="mb-1 text-[11px] font-medium text-muted-foreground/80">
                Storybook Mode
                {templateMeta?.label && (
                  <span className="ml-1.5 font-normal text-[#8b7355]">· {templateMeta.label}</span>
                )}
              </p>
              <h1 className="font-serif text-2xl font-light text-[#1c1917] sm:text-3xl">{storybook.title}</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Follow guided prompts by writing or recording. Turn completed answers into chapters for your Legacy Book.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {STORY_STUDIO_PROCESS_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-full border border-[hsl(38_14%_86%)]/80 bg-white/70 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/75"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full text-xs text-[#5c534a]"
                onClick={() => setStartForMeOpen(true)}
                data-testid="storybook-start-for-me"
              >
                Start for me
              </Button>
              {targetPerson && (
                <Link to={personHref({ id: targetPerson.id })}>
                  <Button variant="outline" size="sm" className="rounded-full gap-1.5">
                    <ExternalLink className="h-3.5 w-3.5" /> View person
                  </Button>
                </Link>
              )}
            </div>
          </div>
          <div className="mt-6">
            <StorybookReadinessPanel
              readiness={readiness}
              progress={progress}
              treeId={storybook?.tree_id}
            />
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-8 xl:flex-row xl:gap-8 sm:px-6">
        <aside className="xl:w-56 shrink-0 order-2 xl:order-1">
          <p className="mb-3 px-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/60">
            Chapters
          </p>
          <nav className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
            {chapters.map((ch) => {
              const isActive = ch.id === activeChapterId;
              const pct = ch.total ? Math.round((ch.completed / ch.total) * 100) : 0;
              return (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => { setActiveChapterId(ch.id); setActivePromptIndex(0); }}
                  className={`w-full rounded-xl px-3 py-2.5 text-left text-sm transition ${
                    isActive
                      ? 'bg-[hsl(38_22%_88%)]/90 font-medium text-[hsl(24_14%_16%)] shadow-sm'
                      : 'text-muted-foreground hover:bg-muted/40'
                  }`}
                >
                  <span className="line-clamp-2">{ch.title}</span>
                  <span className="mt-0.5 block text-[10px] tabular-nums opacity-60">
                    {ch.completed}/{ch.total} · {pct}%
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 order-1 xl:order-2">
          {activeChapter && (
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">
              {activeChapter.title}
            </p>
          )}

          {activePrompt ? (
            <div className="rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] shadow-[0_8px_32px_-16px_rgba(28,25,23,0.08)]">
              <div className="border-b border-[hsl(38_14%_88%)]/60 px-6 py-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    Prompt {activePromptIndex + 1} of {chapterPrompts.length}
                  </span>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 rounded-full px-2.5 text-[11px] text-[#8b7355]"
                      onClick={() => setStartForMeOpen(true)}
                    >
                      Start for me
                    </Button>
                    <span className="text-[11px] text-muted-foreground" data-testid="storybook-save-status">
                      {saveStatusLabel(saveStatus)}
                    </span>
                    {activeAnswer?.completed && (
                      <span className="flex items-center gap-1 text-[11px] text-primary">
                        <Check className="h-3 w-3" /> Complete
                      </span>
                    )}
                  </div>
                </div>
                <p className="font-serif text-lg leading-relaxed text-[#1c1917] sm:text-xl">
                  {activePrompt.prompt_text}
                </p>
              </div>

              <div className="grid gap-6 px-6 py-5 lg:grid-cols-[1fr_min(240px,30%)]">
                <div className="space-y-4 min-w-0">
                  <div className="space-y-1.5">
                    <Label htmlFor="sb-title" className="text-xs text-muted-foreground">
                      Memory title <span className="font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="sb-title"
                      value={draftTitle}
                      onChange={(e) => { setDraftTitle(e.target.value); markDirty(); }}
                      placeholder="A short title for this memory…"
                      className="border-[hsl(38_14%_86%)] font-serif"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="sb-body" className="text-xs text-muted-foreground">
                      Write answer
                    </Label>
                    <Textarea
                      id="sb-body"
                      value={draftBody}
                      onChange={(e) => { setDraftBody(e.target.value); markDirty(); }}
                      rows={8}
                      placeholder="Write freely — or record your answer below and transcribe it here…"
                      className="resize-y border-[hsl(38_14%_86%)] font-serif text-[0.95rem] leading-relaxed min-h-[180px]"
                    />
                    <StorybookAnswerMetrics
                      body={draftBody}
                      completed={Boolean(activeAnswer?.completed)}
                      recordingCount={voice.recordingCount}
                      transcriptStatusLabel={voice.transcriptionStatusLabel}
                    />
                  </div>

                  <StorybookPromptVoicePanel
                    voice={voice}
                    fallbackVoiceHref={
                      targetPerson
                        ? `${personHref({ id: targetPerson.id })}?view=stories&focus=voice`
                        : null
                    }
                  />

                  <div className="rounded-xl border border-dashed border-[hsl(38_14%_86%)]/60 bg-muted/5">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-4 py-2.5 text-left text-xs font-medium text-muted-foreground"
                      onClick={() => setMentionsOpen((o) => !o)}
                    >
                      <span className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        People mentioned, optional
                        {linkedPeople.length > 0 ? ` (${linkedPeople.length})` : ''}
                      </span>
                      {mentionsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    {mentionsOpen && (
                      <div className="space-y-2 border-t border-border/30 px-4 pb-3 pt-2">
                        <p className="text-[11px] text-muted-foreground/70">
                          Link anyone who appears in this memory. This does not create or verify a family relationship.
                        </p>
                        {linkedPeople.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {linkedPeople.map((pid) => {
                              const p = treePeople.find((x) => x.id === pid);
                              return (
                                <span
                                  key={pid}
                                  className="inline-flex items-center gap-1 rounded-full bg-primary/8 px-2.5 py-0.5 text-xs text-primary"
                                >
                                  {p ? getDisplayName(p) : pid.slice(0, 8)}
                                  <button type="button" className="opacity-60 hover:opacity-100" onClick={() => removeMention(pid)} aria-label="Remove">×</button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {mentionablePeople.length > 0 && (
                          <div className="flex gap-2">
                            <select
                              value={mentionPick}
                              onChange={(e) => setMentionPick(e.target.value)}
                              className="flex-1 rounded-lg border border-[hsl(38_14%_86%)] bg-white px-2 py-1.5 text-sm"
                            >
                              <option value="">Add a person…</option>
                              {mentionablePeople.map((p) => (
                                <option key={p.id} value={p.id}>{getDisplayName(p)}</option>
                              ))}
                            </select>
                            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={addMention} disabled={!mentionPick}>
                              Add
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {storybook?.tree_id ? (
                    <StorybookPossessionLink
                      treeId={storybook.tree_id}
                      targetPersonId={storybook.target_person_id || targetPerson?.id}
                      treePeople={treePeople}
                      linkedPossessionId={linkedPossessionId}
                      onLinkedPossessionChange={(id) => {
                        setLinkedPossessionId(id);
                        markDirty();
                      }}
                    />
                  ) : null}
                </div>

                <StorybookAnswerGuidance promptText={activePrompt.prompt_text} />
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-[hsl(38_14%_88%)]/60 px-6 py-4">
                <Button
                  type="button"
                  className="rounded-full gap-1.5"
                  disabled={saveStatus === 'saving' || (!draftBody.trim() && voice.recordingCount === 0)}
                  onClick={async () => {
                    const ok = await saveAnswer({ completed: true });
                    if (ok) goNextPrompt();
                  }}
                >
                  {saveStatus === 'saving' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Mark complete
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  disabled={saveStatus === 'saving'}
                  onClick={() => saveAnswer({ completed: false })}
                >
                  Save answer
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="rounded-full gap-1 text-muted-foreground"
                  disabled={saveStatus === 'saving'}
                  onClick={async () => {
                    const ok = await saveAnswer({ skipped: true });
                    if (ok) goNextPrompt();
                  }}
                >
                  <SkipForward className="h-4 w-4" /> Skip for now
                </Button>
                <div className="ml-auto flex gap-1">
                  <Button type="button" variant="ghost" size="icon" className="rounded-full" onClick={goPrevPrompt} aria-label="Previous prompt">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" className="rounded-full" onClick={goNextPrompt} aria-label="Next prompt">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-[hsl(38_14%_86%)] px-8 py-12 text-center text-muted-foreground">
              <BookOpen className="mx-auto mb-3 h-8 w-8 opacity-40" />
              <p>Select a chapter to begin.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-4 rounded-full"
                onClick={() => setStartForMeOpen(true)}
              >
                Start for me
              </Button>
            </div>
          )}
        </main>

        <div className="order-3 xl:order-3">
          <StorybookPremiumSidebar
            storybookId={storybookId}
            storybook={storybook}
            activeChapterId={activeChapterId}
            chapterCompletedCount={chapterCompletedCount}
            journeySettings={journeySettings}
            nextSuggestedPrompt={nextSuggestedPrompt}
            readiness={readiness}
            onJourneyUpdated={handleJourneyUpdated}
            onGoToPrompt={goToPrompt}
            onStartInterview={startInterviewAtCurrent}
          />
        </div>
      </div>
      </div>

      <Dialog open={startForMeOpen} onOpenChange={setStartForMeOpen}>
        <DialogContent className="max-w-lg gap-0 overflow-hidden p-0 sm:max-w-lg" data-testid="storybook-start-for-me-dialog">
          <StartForMePromptBuilder
            person={targetPerson || undefined}
            personId={targetPerson?.id || storybook?.target_person_id}
            storybookContext={{
              type: storybook?.storybook_type,
              memorial: Boolean(targetPerson?.death_date),
            }}
            defaultExpanded
            className="border-0 shadow-none"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StorybookWorkspace;
